import { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppShell, type AppView } from "./components/AppShell";
import { Button, Card, LoadingState } from "./components/ui";
import { useAppData } from "./hooks/useAppData";
import { useWebMcp } from "./hooks/useWebMcp";
import { LoginPage, UpdatePasswordPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { WorkLogPage } from "./pages/WorkLogPage";
import { ClientsPage } from "./pages/ClientsPage";
import { BudgetPage } from "./pages/BudgetPage";
import { ToolsPage } from "./pages/ToolsPage";
import { PlanPage } from "./pages/PlanPage";
import { SettingsPage } from "./pages/SettingsPage";
import { MorePage } from "./pages/MorePage";

function AuthenticatedApp() {
  const { profile, signOut } = useAuth();
  const appData = useAppData(Boolean(profile));
  const [view, setView] = useState<AppView>("dashboard");
  const [newWorkToken, setNewWorkToken] = useState(0);

  useWebMcp({ data: appData.data, profile: profile!, saveActivity: appData.saveActivity });

  if (!profile) return null;
  if (appData.loading) return <LoadingState />;
  if (appData.error) {
    return (
      <main className="single-card-page">
        <Card className="error-card">
          <span className="error-icon"><AlertTriangle size={26} /></span>
          <h1>Could not load the workspace</h1>
          <p>{appData.error}</p>
          <p className="muted">If this is the first launch, run the included Supabase schema and seed files, then try again.</p>
          <div className="form-actions"><Button variant="ghost" onClick={() => void signOut()}>Sign out</Button><Button onClick={() => void appData.refresh()}><RefreshCw size={17} /> Try again</Button></div>
        </Card>
      </main>
    );
  }

  const openNewWork = () => {
    setView("work");
    setNewWorkToken((value) => value + 1);
  };

  let page;
  switch (view) {
    case "work":
      page = <WorkLogPage data={appData.data} profile={profile} openNewToken={newWorkToken} saveActivity={appData.saveActivity} deleteActivity={appData.deleteActivity} />;
      break;
    case "clients":
      page = <ClientsPage data={appData.data} saveClient={appData.saveClient} deleteClient={appData.deleteClient} />;
      break;
    case "budget":
      page = <BudgetPage data={appData.data} profile={profile} saveMonthlyCost={appData.saveMonthlyCost} saveAssumption={appData.saveAssumption} />;
      break;
    case "tools":
      page = <ToolsPage data={appData.data} profile={profile} saveTool={appData.saveTool} />;
      break;
    case "plan":
      page = <PlanPage data={appData.data} />;
      break;
    case "settings":
      page = <SettingsPage data={appData.data} profile={profile} saveCompany={appData.saveCompany} />;
      break;
    case "more":
      page = <MorePage onView={setView} />;
      break;
    default:
      page = <DashboardPage data={appData.data} onView={setView} onNewWork={openNewWork} />;
  }

  return <AppShell view={view} onView={setView} profile={profile} onSignOut={() => void signOut()}>{page}</AppShell>;
}

function AppContent() {
  const { session, profile, loading, passwordRecovery } = useAuth();
  if (loading) return <LoadingState label="Checking secure access" />;
  if (passwordRecovery && session) return <UpdatePasswordPage />;
  if (!session || !profile) return <LoginPage />;
  return <AuthenticatedApp />;
}

export default function App() {
  return <AuthProvider><AppContent /></AuthProvider>;
}
